import { creditSelectors } from '@makemydeal/dr-platform-shared';
import type { CreditTierScoreHighLow } from '@makemydeal/dr-dash-store/types/types';

export type CreditTierKey = keyof typeof creditSelectors.creditTierRanges;

export interface CreditTierOption {
    value: CreditTierKey;
    label: string;
    score?: number;
}

export const ALL_CREDIT_TIERS_VALUE = 'all';

export const ALL_CREDIT_TIERS_OPTION = {
    value: ALL_CREDIT_TIERS_VALUE,
    label: 'All Credit Tiers'
};

const tierAverageScores = creditSelectors.getCreditTierAverageScores();
const tierRanges = creditSelectors.creditTierRanges;

export const CREDIT_TIER_OPTIONS: CreditTierOption[] = (
    Object.entries(creditSelectors.getCreditTiers()) as [CreditTierKey, string][]
).map(([key, label]) => ({
    value: key,
    label,
    score: tierAverageScores[key]
}));

export const getCreditTierForScore = (score: number): CreditTierKey | undefined => {
    const match = (Object.entries(tierRanges) as [CreditTierKey, number[]][]).find(
        ([, range]) => score >= range[0] && score <= range[1]
    );
    return match?.[0];
};

export const isValidScore = (n: number | undefined): n is number => n !== undefined && Number.isFinite(n) && n > 0;

export const getCustomerCreditScore = (manualScore: number | undefined, tierScore: CreditTierScoreHighLow): number | undefined => {
    if (isValidScore(manualScore)) return manualScore;
    const tierAvg = Math.round((tierScore.low + tierScore.high) / 2);
    return isValidScore(tierAvg) ? tierAvg : undefined;
};

export const getCustomerCreditTier = (
    manualScore: number | undefined,
    tierScore: CreditTierScoreHighLow
): CreditTierKey | undefined => {
    const score = getCustomerCreditScore(manualScore, tierScore);
    return score === undefined ? undefined : getCreditTierForScore(score);
};

export const getScoreForTier = (
    tier: string | undefined,
    customerTier: CreditTierKey | undefined,
    customerScore: number | undefined
): number | undefined => {
    if (!tier) return undefined;
    if (tier === customerTier && isValidScore(customerScore)) return customerScore;
    return CREDIT_TIER_OPTIONS.find((opt) => opt.value === tier)?.score;
};
